import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import ReviewCard from '@/components/ReviewCard';
import StarRating from '@/components/StarRating';
import { Star, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReviewsSection = ({ userType, userId, showManagement = false, token = null }) => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ average_rating: 0, total_reviews: 0 });
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const limit = 20;

  const fetchedRef = useRef(false);

  useEffect(() => {
    if (userId && !fetchedRef.current) {
      fetchedRef.current = true;
      fetchReviews();
    }
  }, [userType, userId]);

  const fetchReviews = async (newOffset = 0) => {
    if (!userId) {
      console.error('Cannot fetch reviews: userId is missing');
      setLoading(false);
      return;
    }

    try {
      const params = {
        limit,
        offset: newOffset,
        include_hidden: showManagement
      };

      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.get(`${API}/reviews/${userType}/${userId}`, {
        params,
        headers
      });

      if (newOffset === 0) {
        setReviews(response.data.reviews);
      } else {
        setReviews(prev => [...prev, ...response.data.reviews]);
      }

      setStats({
        average_rating: response.data.average_rating,
        total_reviews: response.data.total_reviews
      });
      setHasMore(response.data.has_more);
      setOffset(newOffset);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
      console.error('Error details:', {
        userType,
        userId,
        error: error.response?.data || error.message
      });
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    fetchReviews(offset + limit);
  };

  const handleHide = async (reviewId) => {
    if (!token) return;
    
    try {
      await axios.put(`${API}/reviews/${reviewId}/hide`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Review hidden');
      fetchReviews(0);
    } catch (error) {
      console.error('Failed to hide review:', error);
      toast.error('Failed to hide review');
    }
  };

  const handleUnhide = async (reviewId) => {
    if (!token) return;
    
    try {
      await axios.put(`${API}/reviews/${reviewId}/unhide`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Review unhidden');
      fetchReviews(0);
    } catch (error) {
      console.error('Failed to unhide review:', error);
      toast.error('Failed to unhide review');
    }
  };

  const handleVerify = async (reviewId) => {
    if (!token) return;
    
    try {
      await axios.put(`${API}/reviews/${reviewId}/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Review verified');
      fetchReviews(0);
    } catch (error) {
      console.error('Failed to verify review:', error);
      toast.error('Failed to verify review');
    }
  };

  const handleHelpful = async (reviewId, helpful) => {
    try {
      await axios.post(`${API}/reviews/${reviewId}/helpful`, { helpful });
      // Refresh reviews to get updated counts
      fetchReviews(0);
    } catch (error) {
      console.error('Failed to vote:', error);
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-stone-200 rounded w-1/4"></div>
          <div className="h-20 bg-stone-200 rounded"></div>
          <div className="h-20 bg-stone-200 rounded"></div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Reviews Header */}
      <Card className="p-6">
        <h2 className="text-xl font-bold text-stone-900 mb-4 flex items-center gap-2">
          <MessageSquare className="text-[#1A4D2E]" />
          Customer Reviews
        </h2>
        
        {stats.total_reviews > 0 ? (
          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-stone-900 mb-1">
                {stats.average_rating.toFixed(1)}
              </div>
              <StarRating rating={stats.average_rating} size={20} showNumber={false} />
              <p className="text-sm text-stone-600 mt-1">
                Based on {stats.total_reviews} {stats.total_reviews === 1 ? 'review' : 'reviews'}
              </p>
            </div>
            
            {/* Rating breakdown could go here */}
          </div>
        ) : (
          <div className="text-center py-8">
            <Star size={48} className="mx-auto text-stone-300 mb-3" />
            <p className="text-stone-600">No reviews yet</p>
            <p className="text-sm text-stone-500">Be the first to leave a review!</p>
          </div>
        )}
      </Card>

      {/* Reviews List */}
      {reviews.length > 0 && (
        <div className="space-y-3">
          {reviews.map(review => (
            <ReviewCard
              key={review.id}
              review={review}
              showManagement={showManagement}
              onHide={handleHide}
              onUnhide={handleUnhide}
              onVerify={handleVerify}
              onHelpful={handleHelpful}
            />
          ))}
          
          {hasMore && (
            <div className="text-center pt-4">
              <Button
                onClick={handleLoadMore}
                variant="outline"
                className="w-full sm:w-auto"
              >
                Load More Reviews
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReviewsSection;
